import { styled } from '@mui/material/styles';
import { Box, Button, Typography, TextField } from '@mui/material';

// ============================================
// Modal Container
// ============================================
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '760px',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
});

// ============================================
// Header
// ============================================
export const ModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 32px',
  height: '56px',
  backgroundColor: '#262626',
  flexShrink: 0,
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
});

export const CloseIconButton = styled(Box)({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  '&:hover': {
    opacity: 0.8,
  },
});

// ============================================
// Content
// ============================================
export const ModalContentArea = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '24px 32px 32px',
  overflowY: 'auto',
});

export const ContentRow = styled(Box)({
  display: 'flex',
  gap: '20px',
  alignItems: 'stretch',
  height: '420px',
});

// ============================================
// Search
// ============================================
export const SearchInput = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '6px',
    fontSize: '15px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#bfbfbf',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4ab3bc',
    },
  },
});

// ============================================
// Template List
// ============================================
export const ListSection = styled(Box)({
  width: '320px',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  overflow: 'hidden',
});

export const ListHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: '#fafafa',
  borderBottom: '1px solid #e5e5e5',
});

export const ListTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
});

export const ListCount = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4ab3bc',
});

export const ListBody = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#d9d9d9',
    borderRadius: '2px',
  },
});

export const TemplateListItem = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSelected',
})(({ isSelected }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #f2f2f2',
  cursor: 'pointer',
  backgroundColor: isSelected ? 'rgba(74, 179, 188, 0.08)' : '#fff',
  borderLeft: isSelected ? '3px solid #4ab3bc' : '3px solid transparent',
  '&:hover': {
    backgroundColor: isSelected ? 'rgba(74, 179, 188, 0.12)' : '#fafafa',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const TemplateName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '4px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const TemplatePreview = styled(Typography)({
  fontSize: '13px',
  fontWeight: 400,
  color: '#737373',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

export const EmptyText = styled(Typography)({
  fontSize: '14px',
  color: '#a6a6a6',
  textAlign: 'center',
  padding: '40px 16px',
});

// ============================================
// Detail Preview
// ============================================
export const DetailSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  overflow: 'hidden',
});

export const DetailHeader = styled(Box)({
  padding: '12px 16px',
  backgroundColor: '#fafafa',
  borderBottom: '1px solid #e5e5e5',
});

export const DetailTitle = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
});

export const DetailBody = styled(Box)({
  flex: 1,
  padding: '16px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#d9d9d9',
    borderRadius: '2px',
  },
});

export const DetailContentText = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#262626',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  lineHeight: 1.6,
});

export const DetailPlaceholder = styled(Typography)({
  fontSize: '14px',
  color: '#a6a6a6',
  textAlign: 'center',
  paddingTop: '40px',
});

// ============================================
// Footer Buttons
// ============================================
export const ButtonRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  alignItems: 'center',
});

export const CancelButton = styled(Button)({
  width: '120px',
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#d9d9d9',
  color: '#262626',
  fontSize: '18px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#c0c0c0',
    boxShadow: 'none',
  },
});

export const AddButton = styled(Button)({
  width: '160px',
  height: '48px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '18px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#3a9fa8',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: '#e5e5e5',
    color: '#a6a6a6',
  },
});
